import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FieldOption, SearchSelectFieldType, FieldData } from 'types/FieldTypes';
import FormFieldContext from '../FormFieldContext';
import { useGlobalContext } from 'GlobalContext';
import SearchSelect from '../basefields/SearchSelect';
import FieldWrapper from '../FieldWrapper';
import FieldViewMode from './elements/FieldViewMode';
import '../../../style/scss/forms.scss';

const SearchSelectField: React.FC<SearchSelectFieldType & { data: FieldData, viewKey: string }> = ({ 
    viewKey, 
    query, 
    postEndpoint, 
    placeholder, 
    objectName, 
    name, 
    data, 
    defaultValue, 
    directOptionsTitle,
    selectionFormat = 'name',
    selectionSuffix,
    optionFormat,
    label, 
    helperText, 
    tooltipText, 
    viewInEditMode, 
    onlyAddUniqueItems = false, 
    disableAddNew = false, 
    disabled, 
    alignment, 
    isEditable, 
    viewModeLinkUrl, 
    viewModeLinkClick,
    addNewItem, 
    additionalSubmitDataField, 
    storeWholeObject = false, 
    refetchData, 
    handleSubmit,
    showOptionsOnClick
}) => {
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const { setUnsavedChanges, setPreventClosing } = useGlobalContext();
    const [ currentValue, setCurrentValue ] = useState<FieldOption | null>(null);

    // Merge the fetched data with the updated data, so query functions also see the existing values
    const queryData = useMemo(() => ({ ...(data || {}), ...updatedData }), [data, updatedData]);

    // Resolve the query function or param
    const resolvedQuery = typeof query === 'function' ? query(queryData) : query;

    // Set the current selected id and current value from the received data
    useEffect(() => {
        // Determine if the key exists in the updated data, or fallback to the existing data
        const latestValue = name in updatedData ? updatedData[name] : data?.[name];

        // Set the current value and keep the other fields of the object, so they stay available for the selection suffix
        if (latestValue && latestValue.id) {
            setCurrentValue({
                ...latestValue,
                id: latestValue.id,
                value: latestValue[selectionFormat] ?? latestValue.name,
                ...(latestValue[selectionFormat] !== undefined && {
                    [selectionFormat]: latestValue[selectionFormat]
                })
            });

        } else if (defaultValue) {

            // Set the default value as current value, keeping its other fields for the selection suffix
            setCurrentValue({
                ...defaultValue,
                id: defaultValue.id,
                value: defaultValue.value,
                [selectionFormat]: defaultValue.value,
                // name: defaultValue.value
            });

            // Set the id of the current value in the updated data
            setUpdatedData({ ...updatedData, [name]: defaultValue.id });
        }
    }, [data, defaultValue]);

    // Handle select change
    const handleSelectChange = (selectedOption: FieldOption, newItem?: string) => {

        // Configure the submit data
        const submitData = {
            [name]: storeWholeObject ? selectedOption : selectedOption.id,
            ...(additionalSubmitDataField && { 
                [additionalSubmitDataField]: selectedOption[additionalSubmitDataField] 
            }),
        }

        // If a new item is created, handle submit directly afterwards
        if (newItem === 'new' && handleSubmit) {
            handleSubmit(submitData);
        }

        // If an existing item is selected, set updated data and unsaved changes
        else {
            // Set the updated data
            setUpdatedData({ ...updatedData, ...submitData });

            // Set unsaved changes
            if (selectedOption !== undefined) {
                setUnsavedChanges(viewKey, true)
            }
        }
    }

    // Remove unlinked item from the updated data
    const handleUnlink = () => {
        setCurrentValue(null);
        setUpdatedData({ ...updatedData, [name]: null });
    }

    // Handle setting the prevent closing state, or enabling it again
    const handleSetPreventClosing = (shouldPreventClose: boolean) => {
        setPreventClosing(viewKey, shouldPreventClose)
    }

    // Determine the link url based on a given link function or fixed link url string
    const configureLinkUrl = viewModeLinkUrl && data && data[name] ? viewModeLinkUrl.function ? viewModeLinkUrl.function(data[name]) : `${viewModeLinkUrl.baseUrl}/${data[name].id}` : null;

    // Open the linked object through the given click handler, without toggling the edit mode of the field
    const handleViewModeLinkClick = viewModeLinkClick && data && data[name] ? (event: React.MouseEvent) => {
        event.stopPropagation();
        viewModeLinkClick(data[name]);
    } : undefined;

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) ? (
                // Edit mode
                <SearchSelect
                    type='searchselect'
                    name={name}
                    data={data}
                    placeholder={placeholder}
                    value={currentValue}
                    updatedData={queryData}
                    query={resolvedQuery}
                    postEndpoint={postEndpoint}
                    objectName={objectName} 
                    onlyAddUniqueItems={onlyAddUniqueItems}
                    disabled={disabled}
                    disableAddNew={disableAddNew} 
                    showOptionsOnClick={showOptionsOnClick}
                    directOptionsTitle={directOptionsTitle}
                    onSelect={handleSelectChange}
                    selectionFormat={selectionFormat}
                    selectionSuffix={selectionSuffix}
                    optionFormat={optionFormat}
                    onUnlink={handleUnlink}
                    addNewItem={addNewItem}
                    onPreventClose={(shouldPreventClose) => handleSetPreventClosing(shouldPreventClose)}
                    refetchData={() => {if (refetchData) refetchData()}}
                />
            ) : (
                currentValue && (
                    // View mode
                    <FieldViewMode 
                        value={String(currentValue.value)}
                        type='link'
                        linkUrl={configureLinkUrl}
                        onLinkClick={handleViewModeLinkClick}
                        alignment={alignment}
                    />
                )
                
            )}
        </FieldWrapper>
    );
};

export default SearchSelectField;